import type { TaxScenarioInput } from '@/tax/types';
import { ROTH_PLAN_BRACKETS, ROTH_PLAN_MEDICARE_TIERS, type RothPlanApproach, type RothPlanSettings } from '@/tax/rothPlan';
import { FIA_STRATEGIES, fiaAnnualizedReturn, type RothGrowthModel } from '@/tax/rothGrowth';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarInput } from '../fields';

/**
 * The strategy card's Roth section in multi-year mode (`roth_plan` flag, the
 * Multi-year checkbox in the section header). It replaces the single-conversion
 * controls rather than sitting under them: every one of those is a one-year
 * version of something asked here across years (Drew, 2026-10-23). Growth is the
 * card's own Growth Rate box and FIA switch, moved in here unchanged. Settings
 * only; the year-by-year run is a later step.
 */
export function RothPlanBlock({ base, plan, onChange, growth, readOnly }: {
  base: TaxScenarioInput;
  plan: RothPlanSettings;
  onChange: (plan: RothPlanSettings) => void;
  /** The card's Roth growth controls, shared with single-conversion mode. */
  growth: {
    rate: number;
    model: RothGrowthModel;
    fiaOffered: boolean;
    onRate: (rate: number) => void;
    onModel: (model: RothGrowthModel) => void;
  };
  readOnly?: boolean;
}) {
  const set = (patch: Partial<RothPlanSettings>) => onChange({ ...plan, ...patch });
  const married = base.filingStatus === 'MFJ';
  const born = [base.tpBirthYear, married ? base.spBirthYear : undefined].filter((y): y is number => !!y);
  const isFia = growth.fiaOffered && growth.model !== 'straight';
  const fiaRate = isFia ? fiaAnnualizedReturn(growth.model as Exclude<RothGrowthModel, 'straight'>) : null;

  return (
    <div className="space-y-4" data-tour="roth-plan" data-testid="roth-plan-block">
      <p className="text-[10px] text-muted-foreground">
        Convert over several years instead of once. Each year runs through the engine; the comparison page shows the years side by side.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Approach">
          <Select value={plan.approach} onValueChange={v => set({ approach: v as RothPlanApproach })} disabled={readOnly}>
            <SelectTrigger className="h-9 text-sm" aria-label="Approach"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fillBracket">Fill to a bracket each year</SelectItem>
              <SelectItem value="fixed">Fixed amount each year</SelectItem>
              <SelectItem value="spread">Spread evenly to the end year</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {plan.approach === 'fillBracket' && (
          <Field label="Bracket">
            <Select value={String(plan.bracketRate)} onValueChange={v => set({ bracketRate: Number(v) })} disabled={readOnly}>
              <SelectTrigger className="h-9 text-sm" aria-label="Bracket"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROTH_PLAN_BRACKETS.map(r => <SelectItem key={r} value={String(r)}>{Math.round(r * 100)}%</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        )}
        {plan.approach === 'fixed' && (
          <Field label="Amount each year ($)">
            <DollarInput value={plan.fixedAmount} onValueChange={v => set({ fixedAmount: v })} className="h-9 text-sm font-mono" disabled={readOnly} aria-label="Amount each year" />
          </Field>
        )}
        <Field label="From year">
          <YearInput value={plan.fromYear} onChange={v => set({ fromYear: v })} disabled={readOnly} label="From year" />
        </Field>
        <Field label="To year">
          <YearInput value={plan.toYear} onChange={v => set({ toYear: v })} disabled={readOnly} label="To year" />
        </Field>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label={isFia ? 'Annualized Return (%)' : 'Growth Rate (%)'}>
          {isFia ? (
            <p className="h-9 flex items-center font-mono text-sm font-bold">{((fiaRate ?? 0) * 100).toFixed(2)}%</p>
          ) : (
            <Input
              type="number"
              step="0.01"
              value={(growth.rate * 100).toFixed(1)}
              onChange={e => growth.onRate((Number(e.target.value) || 0) / 100)}
              className="h-9 text-sm font-mono"
              placeholder="6"
              disabled={readOnly}
              aria-label="Growth Rate"
            />
          )}
        </Field>
        {growth.fiaOffered && (
          <>
            <label className="flex items-center gap-2 cursor-pointer pt-5" data-tour="roth-fia">
              <Switch checked={isFia} disabled={readOnly} onCheckedChange={on => growth.onModel(on ? 'fia1' : 'straight')} aria-label="Use FIA Strategy" />
              <span className="text-xs font-medium">Use FIA Strategy</span>
            </label>
            {isFia && (
              <Field label="FIA model">
                <Select value={growth.model} onValueChange={v => growth.onModel(v as RothGrowthModel)} disabled={readOnly}>
                  <SelectTrigger className="h-9 text-sm" aria-label="FIA model"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fia1">{FIA_STRATEGIES.fia1.label}</SelectItem>
                    <SelectItem value="fia2">{FIA_STRATEGIES.fia2.label}</SelectItem>
                    <SelectItem value="fia3">{FIA_STRATEGIES.fia3.label}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
          </>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Spending per year ($)">
          <DollarInput value={plan.spendingPerYear} onValueChange={v => set({ spendingPerYear: v })} className="h-9 text-sm font-mono" disabled={readOnly} aria-label="Spending per year" />
        </Field>
        <Field label="Stay under Medicare tier">
          <Select value={String(plan.medicareTierCap)} onValueChange={v => set({ medicareTierCap: Number(v) })} disabled={readOnly}>
            <SelectTrigger className="h-9 text-sm" aria-label="Stay under Medicare tier"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Ignore</SelectItem>
              {ROTH_PLAN_MEDICARE_TIERS.map(t => <SelectItem key={t} value={String(t)}>Stay below tier {t}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <label className="flex items-center gap-1.5 cursor-pointer pt-5">
          <Checkbox checked={plan.qcd} onCheckedChange={v => set({ qcd: v === true })} className="h-3.5 w-3.5" disabled={readOnly} aria-label="Give through QCDs after 70½" />
          <span className="text-[10px] text-muted-foreground">Give through QCDs after 70½</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer pt-5">
          <Checkbox checked={plan.payTaxFromBrokerage} onCheckedChange={v => set({ payTaxFromBrokerage: v === true })} className="h-3.5 w-3.5" disabled={readOnly} aria-label="Pay the tax from brokerage" />
          <span className="text-[10px] text-muted-foreground">Pay the tax from brokerage</span>
        </label>
      </div>

      <p className="text-[10px] uppercase tracking-widest text-muted-foreground pt-1">
        Household
        <span className="normal-case tracking-normal">
          {' · '}
          {born.length
            ? `born ${born.join(' / ')} (Filing & Personal)`
            : 'add birth years on Filing & Personal for the RMD start age and the Medicare year'}
        </span>
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <AgePair label="Retire at" tp={plan.tpRetireAge} sp={plan.spRetireAge} married={married} readOnly={readOnly}
          onTp={v => set({ tpRetireAge: v })} onSp={v => set({ spRetireAge: v })} />
        <AgePair label="Social Security at" tp={plan.tpSsClaimAge} sp={plan.spSsClaimAge} married={married} readOnly={readOnly}
          onTp={v => set({ tpSsClaimAge: v })} onSp={v => set({ spSsClaimAge: v })} />
        <AgePair label="Plan to age" tp={plan.tpPlanToAge} sp={plan.spPlanToAge} married={married} readOnly={readOnly}
          onTp={v => set({ tpPlanToAge: v })} onSp={v => set({ spPlanToAge: v })} />
      </div>

      <p className="text-[10px] uppercase tracking-widest text-muted-foreground pt-1">Accounts</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label={married ? 'Traditional IRA / 401(k), taxpayer' : 'Traditional IRA / 401(k)'}>
          <DollarInput value={plan.traditionalIraTp} onValueChange={v => set({ traditionalIraTp: v })} className="h-9 text-sm font-mono" disabled={readOnly} aria-label="Traditional IRA taxpayer" />
        </Field>
        {married && (
          <Field label="Traditional IRA / 401(k), spouse">
            <DollarInput value={plan.traditionalIraSp} onValueChange={v => set({ traditionalIraSp: v })} className="h-9 text-sm font-mono" disabled={readOnly} aria-label="Traditional IRA spouse" />
          </Field>
        )}
        <Field label="Roth">
          <DollarInput value={plan.rothBalance} onValueChange={v => set({ rothBalance: v })} className="h-9 text-sm font-mono" disabled={readOnly} aria-label="Roth balance" />
        </Field>
        <Field label="Brokerage">
          <DollarInput value={plan.brokerageBalance} onValueChange={v => set({ brokerageBalance: v })} className="h-9 text-sm font-mono" disabled={readOnly} aria-label="Brokerage balance" />
        </Field>
        <Field label="Brokerage cost basis">
          <DollarInput value={plan.brokerageBasis} onValueChange={v => set({ brokerageBasis: v })} className="h-9 text-sm font-mono" disabled={readOnly} aria-label="Brokerage cost basis" />
        </Field>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function YearInput({ value, onChange, disabled, label }: { value: number; onChange: (v: number) => void; disabled?: boolean; label: string }) {
  return (
    <Input
      type="number"
      step={1}
      value={value || ''}
      onChange={e => onChange(Math.round(Number(e.target.value)) || 0)}
      className="h-9 text-sm font-mono"
      disabled={disabled}
      aria-label={label}
    />
  );
}

function AgePair({ label, tp, sp, married, readOnly, onTp, onSp }: {
  label: string; tp: number; sp: number; married: boolean; readOnly?: boolean;
  onTp: (v: number) => void; onSp: (v: number) => void;
}) {
  const age = (v: string) => Math.max(0, Math.round(Number(v)) || 0);
  return (
    <Field label={married ? `${label} (TP / SP)` : label}>
      <div className="flex gap-1.5">
        <Input type="number" step={1} value={tp || ''} onChange={e => onTp(age(e.target.value))} className="h-9 text-sm font-mono" disabled={readOnly} aria-label={`${label}, taxpayer`} />
        {married && <Input type="number" step={1} value={sp || ''} onChange={e => onSp(age(e.target.value))} className="h-9 text-sm font-mono" disabled={readOnly} aria-label={`${label}, spouse`} />}
      </div>
    </Field>
  );
}
